'use client';

import { useEffect, useState } from 'react';
import { Star, MessageCircle, Send } from 'lucide-react';
import { ReviewList } from './review-list';
import { ReviewForm } from './review-form';
import { checkLoginStatus, getUserData, logoutUser } from '../../api/authService';
import { getBook, addBook, getReviews, addReview, markHelpful, type Review, type Book } from '../../api/bookService';

interface BookDetailProps {
  bookId: string;
}

// Default mock book data (fallback)
const defaultBook: Book = {
  id: 1,
  title: 'Lord Build Me A House',
  author: 'Latangela Rogers',
  isbn: '978-1945558009',
  year: '2016',
  pages: '124',
  language: 'English',
  content: `Upon this rock I shall build my church; and the gates of hell shall not prevail against it.

--Matthew 16:18

We all have a time to be called by God, and we have the choice to accept Him or reject Him. Lord, Build Me a House is a book designed to educate and guide readers to a higher standard of Kingdom living. Author Latangela Rogers provides a blueprint of how our temples are broken down and built back up in God's image. The rebuilding process is never easy, but once it is complete, we become a vessel for His use. We were all born into a life of sin, but God's love for each of us allows us to trade our life for His and begin to carry out the plan that He has predestined for us.

Are you ready for the rebuilding of your temple? Are you ready to accept destiny and purpose? If so, let's journey through the process together.`,
  thumbnail: 'https://www.thetransformationcollective.org/wp-content/uploads/2025/11/IMG_7650.jpg',
};

export function BookDetail_Component({ bookId }: BookDetailProps) {
  const [bookData, setBookData] = useState<Book | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     addBook({
//       title: 'Lord Build Me A House',
//       author: 'Latangela Rogers',
//       isbn: '978-1945558009',
//       year: '2016',
//       pages: '124',
//       language: 'English',
//       content: `Upon this rock I shall build my church; and the gates of hell shall not prevail against it.

// --Matthew 16:18

// We all have a time to be called by God, and we have the choice to accept Him or reject Him. Lord, Build Me a House is a book designed to educate and guide readers to a higher standard of Kingdom living. Author Latangela Rogers provides a blueprint of how our temples are broken down and built back up in God's image. The rebuilding process is never easy, but once it is complete, we become a vessel for His use. We were all born into a life of sin, but God's love for each of us allows us to trade our life for His and begin to carry out the plan that He has predestined for us.

// Are you ready for the rebuilding of your temple? Are you ready to accept destiny and purpose? If so, let's journey through the process together.`,
//       thumbnail: 'https://www.thetransformationcollective.org/wp-content/uploads/2025/11/IMG_7650.jpg',
//     })
//   }, [])

useEffect(() => {
  // logoutUser();
}, [])

  useEffect(() => {
    const fetchUserData = async () => {
      const loggedIn = await checkLoginStatus();
      if (!loggedIn) {
        return;
      }
      try {
        const userData = await getUserData();
        if (!userData) {
          return;
        }
        setUser(userData);
      } catch (error) {
        console.log('Error fetching user:', error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const bookNum = parseInt(bookId, 10);
        const [book, reviewsData] = await Promise.all([
          getBook(bookNum),
          getReviews(bookNum)
        ]);

        if (book) {
          setBookData(book);
        } else {
          setBookData(defaultBook);
        }

        setReviews(reviewsData || []);
      } catch (error) {
        console.error('Error loading book data:', error);
        setBookData(defaultBook);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };

    if (bookId) {
      loadData();
    }
  }, [bookId]);

  const handleAddReview = async (newReview: {
    author: string;
    rating: number;
    title: string;
    content: string;
  }) => {
    try {
      const bookNum = parseInt(bookId, 10);
      await addReview({
        book_id: bookNum,
        title: newReview.title,
        content: newReview.content,
        rating: newReview.rating,
      });

      // Reload reviews after successful submission
      const updatedReviews = await getReviews(bookNum);
      setReviews(updatedReviews || []);
      setShowReviewForm(false);
    } catch (error) {
      console.error('Error adding review:', error);
      alert('Failed to add review. Please try again.');
    }
  };

  const handleMarkHelpful = async (reviewId: number) => {
    try {
      const result = await markHelpful(reviewId);
      if (result.success) {
        // Update the review in local state
        setReviews(reviews.map(review =>
          review.id === reviewId
            ? { ...review, helpful: result.helpful }
            : review
        ));
      }
    } catch (error) {
      console.error('Error marking review as helpful:', error);
      alert('Failed to mark as helpful. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <p className="text-slate-300">Loading...</p>
      </div>
    );
  }

  const displayBook = bookData || defaultBook;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-20">
      {/* Hero Section */}
      <div className="relative overflow-hidden border-b border-slate-700">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(100,116,139,0.1)_0%,rgba(51,65,85,0.1)_100%)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-3 lg:gap-12">
            {/* Book Cover */}
            <div className="flex justify-center md:col-span-1">
              <div className="group relative transition-transform duration-500 ease-out hover:-translate-y-2">
                <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-red-400 to-orange-500 blur-xl opacity-0 transition-opacity duration-500 group-hover:opacity-30" />
                <img
                  src={displayBook.thumbnail || "/placeholder.svg"}
                  alt={displayBook.title}
                  className="relative h-auto w-full max-w-sm rounded-lg shadow-2xl transition-shadow duration-500 group-hover:shadow-red-500/50"
                />
              </div>
            </div>

            {/* Book Info */}
            <div className="flex flex-col justify-center md:col-span-2">
              <div className="space-y-6">
                {/* Genre Tags */}
                <div className="flex flex-wrap gap-2">
                  {['Transformation', 'Gospel', 'Growth'].map((genre) => (
                    <span
                      key={genre}
                      className="inline-flex items-center rounded-full bg-red-500/10 px-3 py-1 text-sm font-medium text-red-300 border border-red-500/20 transition-all duration-300 hover:bg-red-500/20 hover:border-red-500/40"
                    >
                      {genre}
                    </span>
                  ))}
                </div>

                {/* Title & Author */}
                <div className="space-y-3">
                  <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
                    {displayBook.title}
                  </h1>
                  <p className="text-xl text-slate-400">by {displayBook.meta?.author}</p>
                </div>
              {/* <p className='text-white'>{JSON.stringify(displayBook)}</p> */}

                {/* Rating */}
                <div className="flex items-center gap-4 py-4 border-y border-slate-700">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          size={20}
                          className={`transition-colors duration-300 ${i < 5
                            ? 'fill-red-400 text-red-400'
                            : 'text-slate-600'
                            }`}
                        />
                      ))}
                    </div>
                    <span className="text-lg font-semibold text-white">
                      5
                    </span>
                  </div>
                  <span className="text-slate-400">
                    {reviews.length} reviews
                  </span>
                </div>

                {/* Book Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg bg-slate-800/50 p-4 backdrop-blur">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Published
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {displayBook.year}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-800/50 p-4 backdrop-blur">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Pages
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {displayBook.pages}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-800/50 p-4 backdrop-blur">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Language
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {displayBook.language}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-800/50 p-4 backdrop-blur">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                      ISBN
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {displayBook.isbn}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description Section */}
      <div className="border-b border-slate-700">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <h2 className="mb-4 text-2xl font-bold text-white">About this book</h2>
          <p className="max-w-3xl text-slate-300 leading-relaxed">
            {displayBook.content}
          </p>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-white flex items-center gap-3">
              <MessageCircle className="text-red-400" />
              Readers' Reviews
            </h2>
          </div>
          <button
            onClick={() => { user ? setShowReviewForm(!showReviewForm) : window.location.href = `/login?id=${bookId}` }}
            className="group relative inline-flex items-center gap-2 rounded-lg bg-red-500 px-6 py-3 font-semibold text-slate-900 transition-all duration-300 hover:bg-red-400 hover:shadow-lg hover:shadow-red-500/50 hover:-translate-y-0.5 active:translate-y-0 active:shadow-red-500/25"
          >
            <Send size={18} className="transition-transform group-hover:translate-x-1" />
            Add Review
          </button>
        </div>

        {/* Add Review Form */}
        {showReviewForm && (
          <div className="mb-8 animate-in fade-in slide-in-from-top-4 duration-300">
            <ReviewForm onSubmit={handleAddReview} />
          </div>
        )}

        {/* Reviews List */}
        <ReviewList reviews={reviews} onMarkHelpful={handleMarkHelpful} />
      </div>
    </div>
  );
}
